import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useMemo } from 'react';
import * as yup from 'yup';
import { isJobType } from '../domain/use-cases/isJobType';
import { IJobEntry, JobType } from '../../../contentful/types/IJob';
import { searchJobs } from '../domain/use-cases/searchJobs';

export interface IJobsSearchState {
	type?: JobType[];
	employerName?: string;
	query?: string;
	pageIndex: number;
}

const pageIndexSchema = yup.number().required().min(0);

export function useJobsSearch(jobs: IJobEntry[]) {
	const router = useRouter();
	const pathname = usePathname();
	const searchParams = useSearchParams();

	const params = {
		type: searchParams.getAll('type'),
		employerName: searchParams.get('employerName'),
		query: searchParams.get('query'),
		pageIndex: searchParams.get('pageIndex'),
	};

	const state: IJobsSearchState = {
		type: params.type.filter(isJobType),
		employerName: params.employerName ?? undefined,
		query: params.query ?? undefined,
		pageIndex: pageIndexSchema.isValidSync(params.pageIndex)
			? +params.pageIndex - 1
			: 0,
	};

	const isActive = state.type || state.employerName || state.query;

	const results = useMemo(() => {
		if (isActive) {
			return searchJobs({ jobs, ...state });
		}

		return null;
	}, [jobs, state]);

	const submit = (newState: Partial<IJobsSearchState>) =>
		router.push(getSubmitHref(newState), { scroll: false });

	const getSubmitHref = (newState: Partial<IJobsSearchState>) => {
		const newParams = new URLSearchParams(searchParams);
		Object.entries({ ...state, ...newState }).forEach(([key, value]) => {
			if (Array.isArray(value)) {
				newParams.delete(key);
				value.forEach((v) => newParams.append(key, String(v)));
			} else if (value) {
				newParams.set(key, String(value));
			} else {
				newParams.delete(key);
			}
		});

		return `${pathname}?${newParams.toString()}`;
	};

	return {
		isActive,
		results,
		state,
		submit,
		getSubmitHref,
	};
}
